import { memo, useState } from 'react';

import { SidebarTrigger } from '@doger/ui/components/ui/sidebar';
import { ThemeSwitcher } from '@doger/ui/theme';
import { useJWT } from '@doger/core/modules/auth';
import type { AIModel } from '@doger/core/modules/chat';
import { LanguageSwitcher } from '@doger/core/shared/i18n';

import { useAnalytics } from '@doger/chat/shared/analytics';

import { useAuth } from '../../auth/auth.context';
import { useCreateNewChatMutation } from '../api/mutations';
import { ChatInput } from '../chat-input.component';
import { ModelSwitcher } from '../choose-model';
import { EmptyChat } from './empty-chat.component';
import styles from './new-chat.module.scss';

const NewChat = memo(() => {
  const jwt = useJWT();
  const [currentModel, setCurrentModel] = useState<AIModel>('grok');
  const { setIsAuthModalOpen } = useAuth();
  const analytics = useAnalytics();

  const createSessionMutation = useCreateNewChatMutation();

  const handleSubmit = async (args: {
    from: 'text' | 'suggestion';
    text: string;
  }) => {
    if (!jwt) {
      setIsAuthModalOpen(true);

      return;
    }

    if (args.text) {
      await createSessionMutation.mutateAsync({
        model: currentModel,
        text: args.text,
        from: args.from,
      });
    }
  };

  return (
    <div className={styles.mainChat}>
      <div className={styles.header}>
        <div className={styles.headerActions}>
          <SidebarTrigger />

          {jwt && (
            <ModelSwitcher
              currentModel={currentModel}
              onModelChange={setCurrentModel}
              disabled={false}
            />
          )}
        </div>

        <div className={styles.headerActions}>
          <ThemeSwitcher
            onThemeChange={(theme) =>
              analytics.amplitude.track({
                event: 'Theme Changed',
                properties: { toTheme: theme },
              })
            }
          />
          <LanguageSwitcher
            onLanguageChange={(language) =>
              analytics.amplitude.track({
                event: 'Language Changed',
                properties: { toLanguage: language },
              })
            }
          />
        </div>
      </div>

      <EmptyChat
        className={styles.emptyChat}
        onSubmit={(text) => handleSubmit({ from: 'suggestion', text })}
        isLoading={createSessionMutation.isPending}
      />

      <div className={styles.footer}>
        <ChatInput
          name='create-new-chat-form'
          isLoading={false}
          onSubmit={(text) => handleSubmit({ from: 'text', text })}
        />
      </div>
    </div>
  );
});

export { NewChat };
